/**
 * cprPicker.js — выбор двух точек в окне и построение изогнутого реформата.
 *
 * ЧЕГО НЕ ХВАТАЛО. Математика CPR в программе была написана целиком:
 * `extractCenterline` (поиск пути по яркости), `buildCPR` (трилинейная выборка
 * вдоль линии), `renderCPRToCanvas`. Не хватало ровно одного — способа задать
 * центральную линию. Единственная кнопка показывала подсказку «задайте
 * центральную линию сосуда», и на этом всё; в 10.19.9 я её убрал по правилу
 * «пустая кнопка хуже отсутствующей». Здесь она возвращается с работой.
 *
 * КАК ЭТО РАБОТАЕТ ДЛЯ ВРАЧА. Нажали кнопку → окно ждёт двух нажатий: начало и
 * конец сосуда. После второго строится линия между ними по яркости (кровь с
 * контрастом, окно 150…600 HU), затем развёртка вдоль неё — сосуд показывается
 * распрямлённым во всю длину.
 *
 * ЧТО СКАЗАНО ВСЛУХ, а не спрятано:
 *
 *  · Если линия прошла НЕ по сосуду (мало точек попало в окно яркости),
 *    программа говорит об этом прямо и не выдаёт длину как измерение.
 *    Признак надёжности считается в самом `extractCenterline` — тот же приём,
 *    что в жёстком совмещении, где неудача возвращает ok:false.
 *  · Длина по такой линии — НЕ измерение сосуда: линия идёт по ярким вокселям,
 *    а не по геометрическому центру, и на извитом сосуде завышает длину.
 *    Для планирования стента этого недостаточно, и об этом написано в окне.
 */

import { getRenderingEngine, getActiveOrFirstViewportId } from '../core/viewportState.js';
import { getVolumeData } from './clinicalTools.js';
import { showToast } from '../utils/helpers.js';
import { СТРОКА as ОГОВОРКА } from '../core/disclaimer.js';
import { log } from '../utils/logger.js';

const ОКНО_КРОВИ = { huMin: 150, huMax: 600 };
const ПОЛУШИРИНА_ММ = 12;      // коридор развёртки: 24 мм хватает на коронар с бляшкой
const ШАГ_ММ = 0.5;

let _режим = null;   // {vpId, точки: [], снять: fn}

function окно() {
  const re = getRenderingEngine();
  if (!re) return null;
  const id = getActiveOrFirstViewportId();
  if (!id) return null;
  const vp = re.getViewport(id);
  if (!vp || typeof vp.canvasToWorld !== 'function') return null;
  if (String(vp.type || '').toLowerCase().includes('3d')) return null;
  return { id, vp };
}

/**
 * Мировые координаты → индекс вокселя.
 *
 * Берём геометрию у того же источника, что и все клинические расчёты
 * (`getVolumeData`): начало объёма, шаг и размеры. Без учёта направления
 * (direction): на косых сериях индекс получится неточным, и это честно
 * записано в оговорке к результату — гадать тут хуже, чем предупредить.
 */
function вИндекс(мир, v) {
  const [ox, oy, oz] = v.origin || [0, 0, 0];
  const [sx, sy, sz] = v.spacing || [1, 1, 1];
  return [
    Math.round((мир[0] - ox) / (sx || 1)),
    Math.round((мир[1] - oy) / (sy || 1)),
    Math.round((мир[2] - oz) / (sz || 1)),
  ];
}

/** Начать выбор двух точек. */
export function начатьВыборТочек() {
  if (_режим) { прекратить(); return; }

  const o = окно();
  if (!o) {
    showToast('Откройте исследование и станьте в плоское окно — точки ставятся в нём', 'warning', 4000);
    return;
  }
  const v = getVolumeData();
  if (!v?.scalarData || !v?.dims) {
    showToast('Объём не загружен — строить линию не по чему', 'warning', 4000);
    return;
  }

  const элемент = o.vp.element;
  const точки = [];
  const наНажатие = (e) => {
    const r = элемент.getBoundingClientRect();
    const canvasPoint = [e.clientX - r.left, e.clientY - r.top];
    let мир;
    try { мир = o.vp.canvasToWorld(canvasPoint); } catch (err) { мир = null; }
    if (!мир) { showToast('Не удалось определить точку в объёме', 'warning', 3000); return; }
    точки.push(вИндекс(мир, v));
    if (точки.length === 1) {
      showToast('Начало отмечено. Отметьте конец сосуда', 'info', 4000);
      return;
    }
    прекратить();
    построить(v, точки[0], точки[1]);
  };

  элемент.addEventListener('click', наНажатие);
  элемент.style.cursor = 'crosshair';
  _режим = {
    vpId: o.id,
    снять: () => {
      элемент.removeEventListener('click', наНажатие);
      элемент.style.cursor = '';
    },
  };
  showToast('Отметьте НАЧАЛО сосуда нажатием в окне (Esc — отмена)', 'info', 6000);

  const наEsc = (e) => {
    if (e.key === 'Escape') { прекратить(); showToast('Выбор точек отменён', 'info', 2000); }
  };
  document.addEventListener('keydown', наEsc, { once: false });
  const прежний = _режим.снять;
  _режим.снять = () => { прежний(); document.removeEventListener('keydown', наEsc); };
}

/** Выйти из режима выбора. */
export function прекратить() {
  if (_режим) { try { _режим.снять(); } catch (e) { /* */ } }
  _режим = null;
}

/** Идёт ли выбор точек — для проверок. */
export function ждётТочки() { return !!_режим; }

/** Построить линию и развёртку по двум индексам. */
async function построить(v, начало, конец) {
  showToast('Ищу линию сосуда между точками…', 'info', 3000);
  let линия, развёртка;
  try {
    const { extractCenterline, buildCPR } = await import('../compute/vtkModule.js');
    линия = extractCenterline(v.scalarData, v.dims, v.spacing || [1, 1, 1], начало, конец, ОКНО_КРОВИ);
    if (!линия?.success) {
      showToast(`Линия не построилась: ${линия?.error || 'путь не найден'}`, 'warning', 5000);
      return;
    }
    if (линия.надёжно === false) {
      // Не строим развёртку по мусорной линии: показать её значило бы выдать
      // случайный разрез за реформат сосуда.
      showToast(`Линия прошла не по сосуду: ${линия.причина}. `
        + 'Отметьте точки внутри контрастированного просвета.', 'warning', 9000);
      return;
    }
    развёртка = buildCPR(v.scalarData, v.dims, v.spacing || [1, 1, 1], линия.path, ПОЛУШИРИНА_ММ, ШАГ_ММ);
    if (!развёртка?.success) {
      showToast(`Развёртка не построилась: ${развёртка?.error || 'неизвестно'}`, 'warning', 5000);
      return;
    }
  } catch (e) {
    log.warn('[CPR]', e);
    showToast(`Развёртка не получилась: ${e.message}`, 'error', 6000);
    return;
  }
  показать(линия, развёртка);
}

/** Окно с развёрткой. Своё, потому что показывать надо картинку и числа рядом. */
async function показать(линия, развёртка) {
  const прежнее = document.getElementById('cpr-window');
  if (прежнее) прежнее.remove();

  const фон = document.createElement('div');
  фон.id = 'cpr-window';
  фон.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;'
    + 'justify-content:center;background:rgba(6,10,18,.85);padding:20px;'
    + "font-family:system-ui,-apple-system,'Segoe UI',sans-serif;";

  const карта = document.createElement('div');
  карта.style.cssText = 'background:#111926;border:1px solid #2a3546;border-radius:14px;padding:16px;'
    + 'color:#cfe0ef;max-width:96vw;max-height:92vh;overflow:auto;';

  const шапка = document.createElement('div');
  шапка.style.cssText = 'font-size:16px;font-weight:700;color:#fff;margin-bottom:8px;';
  шапка.textContent = 'Изогнутый реформат (CPR)';

  const числа = document.createElement('div');
  числа.style.cssText = 'font-size:13px;color:#9fb3cf;margin-bottom:10px;line-height:1.5;';
  числа.textContent = `Длина линии: ${линия.lengthMm} мм · точек: ${линия.pointCount} · `
    + `в просвете: ${Math.round((линия.доляВСосуде ?? 1) * 100)}% · `
    + `развёртка ${развёртка.width}×${развёртка.height} (коридор ${развёртка.physicalWidthMm} мм)`;

  const полотно = document.createElement('canvas');
  полотно.style.cssText = 'display:block;background:#000;border:1px solid #2a3546;border-radius:6px;'
    + 'max-width:92vw;image-rendering:pixelated;';

  const оговорка = document.createElement('div');
  оговорка.style.cssText = 'font-size:11px;color:#eab308;margin-top:10px;max-width:640px;line-height:1.5;';
  оговорка.textContent = `${ОГОВОРКА} Длина по этой линии — не измерение сосуда: `
    + 'линия идёт по ярким вокселям, а не по геометрическому центру, и на извитом сосуде длину завышает. '
    + 'Для планирования стента её недостаточно.';

  const кнопки = document.createElement('div');
  кнопки.style.cssText = 'margin-top:12px;display:flex;gap:8px;flex-wrap:wrap;';
  const мк = (т, основная) => {
    const b = document.createElement('button');
    b.textContent = т;
    b.style.cssText = 'padding:8px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;'
      + (основная ? 'border:1px solid #3b82f6;background:#1d4ed8;color:#fff;'
        : 'border:1px solid #2a3546;background:#16202f;color:#9fb3cf;');
    return b;
  };
  const сохранить = мк('Сохранить PNG', true);
  const закрыть = мк('Закрыть', false);
  кнопки.append(сохранить, закрыть);

  карта.append(шапка, числа, полотно, оговорка, кнопки);
  фон.appendChild(карта);
  document.body.appendChild(фон);

  try {
    const { renderCPRToCanvas } = await import('../compute/vtkModule.js');
    renderCPRToCanvas(развёртка, полотно, 700, 200);   // окно для контрастированной крови
  } catch (e) {
    числа.textContent += ` · отрисовать не удалось: ${e.message}`;
  }

  сохранить.addEventListener('click', () => {
    try {
      const url = полотно.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpr_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
      a.click();
    } catch (e) { showToast('Сохранить не удалось: ' + e.message, 'warning', 4000); }
  });
  закрыть.addEventListener('click', () => фон.remove());
  фон.addEventListener('click', (e) => { if (e.target === фон) фон.remove(); });
}

/** Подключить кнопку. */
export function initCPR() {
  const b = document.getElementById('btn-cpr-vessel');
  if (b) b.addEventListener('click', начатьВыборТочек);
  log.debug('[CPR] кнопка подключена');
}

export default { initCPR, начатьВыборТочек, прекратить, ждётТочки };
